import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_BASE_URL } from '../utils/constants';
import api from './axiosConfig';

const API_URL = API_BASE_URL;
console.log('Using API URL:', API_URL);

export const validateParticipant = async (participantNumber, firstName = '') => {
  try {
    const response = await api.get(`/auth/validate-participant/${participantNumber}?firstName=${encodeURIComponent(firstName)}`);
    return response.data;
  } catch (error) {
    const errorData = error.response?.data;
    let serverMessage = errorData?.details || errorData?.error || error.message || 'Validation failed';
    if (typeof serverMessage === 'object') {
      serverMessage = Object.entries(serverMessage)
        .filter(([_, v]) => v)
        .map(([k, v]) => `${k}: ${v}`)
        .join('\n');
    }
    throw serverMessage;
  }
};

export const registerUser = async (userData) => {
  try {
    console.log('Sending registration request to:', `${API_URL}/auth/register`);
    const response = await api.post('/auth/register', userData);

    // Store token and user data
    await AsyncStorage.setItem('token', response.data.token);

    // Log participant data
    if (response.data.user.participant) {
      console.log('Participant data received:', {
        number: response.data.user.participant.participantNumber,
        fcp: response.data.user.participant.fcp,
        cluster: response.data.user.participant.cluster
      });
    }

    await AsyncStorage.setItem('user', JSON.stringify(response.data.user));
    return response.data;
  } catch (error) {
    console.error('Registration error:', error);
    const errorData = error.response?.data;
    let serverMessage = errorData?.details || errorData?.error || error.message || 'Registration failed';
    if (typeof serverMessage === 'object' && serverMessage !== null) {
      serverMessage = Object.entries(serverMessage)
        .filter(([_, v]) => v)
        .map(([_, v]) => v)
        .join('\n');
    }
    throw serverMessage;
  }
};

export const loginUser = async (email, password) => {
  try {
    console.log('Sending login request to:', `${API_URL}/auth/login`);
    const response = await api.post('/auth/login', { email, password });

    // Store token and user data
    await AsyncStorage.setItem('token', response.data.token);

    // Log participant data
    if (response.data.user.participant) {
      console.log('Participant data received:', {
        number: response.data.user.participant.participantNumber,
        fcp: response.data.user.participant.fcp,
        cluster: response.data.user.participant.cluster
      });
    }

    await AsyncStorage.setItem('user', JSON.stringify(response.data.user));
    return response.data;
  } catch (error) {
    console.error('Login error:', error);
    const errorData = error.response?.data;
    let serverMessage = errorData?.details || errorData?.error || error.message || 'Login failed';
    if (typeof serverMessage === 'object' && serverMessage !== null) {
      serverMessage = Object.entries(serverMessage)
        .filter(([_, v]) => v)
        .map(([_, v]) => v)
        .join('\n');
    }
    throw serverMessage;
  }
};

export const logoutUser = async () => {
  console.log('api/auth: Starting logout');
  try {
    await AsyncStorage.multiRemove(['token', 'user']);
    console.log('api/auth: Successfully removed token and user data');
    return { success: true };
  } catch (error) {
    console.error('api/auth: Logout error:', error);
    throw new Error('Logout failed: ' + error.message);
  }
};

/**
 * Verify current session token and fetch fresh user data
 */
export const verifyToken = async () => {
  try {
    const response = await api.get('/auth/me');
    return response.data;
  } catch (error) {
    console.error('Verify token error:', error);
    throw error;
  }
};

export const updateProfile = async (userData, profileImage = null) => {
  try {
    console.log('Starting profile update...');

    // Create FormData instance
    const formData = new FormData();

    // Handle profile image upload
    if (profileImage) {
      console.log('Processing profile image:', {
        uri: profileImage.uri ? 'Present' : 'Missing',
        type: profileImage.type || 'image/jpeg',
        name: profileImage.name || 'profile.jpg'
      });

      // Ensure proper image object structure
      const imageToUpload = {
        uri: profileImage.uri,
        type: profileImage.type || 'image/jpeg',
        name: profileImage.name || 'profile.jpg'
      };
      formData.append('profileImage', imageToUpload);
    }

    // Add user data fields individually
    Object.keys(userData).forEach(key => {
      const value = userData[key];
      if (value !== null && value !== undefined && key !== 'profileImage' && key !== 'businessPhotos') {
        console.log(`Adding field ${key}:`, value);
        formData.append(key, value.toString());
      }
    });

    // Handle business photos specifically
    if (userData.businessPhotos && Array.isArray(userData.businessPhotos)) {
      userData.businessPhotos.forEach((photoUri, index) => {
        // Only upload if it's a new local URI (doesn't start with http)
        if (photoUri && !photoUri.startsWith('http')) {
          formData.append('businessPhotos', {
            uri: photoUri,
            type: 'image/jpeg',
            name: `business_${index}.jpg`
          });
        }
      });
    }

    // Get current token
    const token = await AsyncStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication token not found');
    }

    // Make API request with increased timeout
    console.log('Making API request...');
    const response = await api.put('/auth/profile', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${token}`
      },
      timeout: 30000, // 30 second timeout for image upload
    });
    console.log('API response received:', response.status);
    console.log('Profile update response data:', {
      message: response.data.message,
      userReceived: !!response.data.user,
      profileImageUrl: response.data.user?.profileImageUrl ? 'Present' : 'Missing'
    });

    await AsyncStorage.setItem('user', JSON.stringify(response.data.user));
    return response.data;
  } catch (error) {
    console.error('Profile update error:', error);
    const errorData = error.response?.data;
    let serverMessage = errorData?.details || errorData?.error || error.message || 'Profile update failed';
    if (typeof serverMessage === 'object') {
      serverMessage = Object.entries(serverMessage)
        .filter(([_, v]) => v)
        .map(([k, v]) => `${v}`)
        .join('\n');
    }
    throw serverMessage;
  }
};

export const changePassword = async (currentPassword, newPassword) => {
  try {
    const response = await api.post('/auth/change-password', {
      currentPassword,
      newPassword
    });
    return response.data;
  } catch (error) {
    console.error('Password change error:', error);
    throw error.error || error.message || 'Password change failed';
  }
};

export const getMentors = async () => {
  try {
    const response = await api.get('/user/mentors');
    return response.data;
  } catch (error) {
    console.error('Fetch mentors error:', error);
    throw error.response?.data?.error || error.message || 'Failed to fetch mentors';
  }
};

export const assignMentorStatus = async (userId, data) => {
  try {
    const response = await api.post(`/admin/users/${userId}/mentor`, data);
    return response.data;
  } catch (error) {
    console.error('Assign mentor error:', error);
    throw error.response?.data?.error || error.message || 'Failed to assign mentor status';
  }
};

export const getAccountStats = async () => {
  try {
    const response = await api.get('/user/account-stats');
    return response.data;
  } catch (error) {
    console.error('Fetch account stats error:', error);
    throw error.response?.data?.error || error.message || 'Failed to fetch account stats';
  }
};

export const getLeaderboard = async () => {
  try {
    const response = await api.get('/user/leaderboard');
    return response.data;
  } catch (error) {
    console.error('Fetch leaderboard error:', error);
    throw error.response?.data?.error || error.message || 'Failed to fetch leaderboard';
  }
};

export const getGamificationRules = async () => {
  try {
    const response = await api.get('/user/gamification-rules');
    return response.data;
  } catch (error) {
    console.error('Fetch gamification rules error:', error);
    throw error.response?.data?.error || error.message || 'Failed to fetch rules';
  }
};

export const getUserAwards = async () => {
  try {
    const response = await api.get('/user/awards');
    return response.data;
  } catch (error) {
    console.error('Fetch user awards error:', error);
    throw error.response?.data?.error || error.message || 'Failed to fetch awards';
  }
};

export const getAvailableAwards = async () => {
  try {
    const response = await api.get('/user/available-awards');
    return response.data;
  } catch (error) {
    console.error('Fetch available awards error:', error);
    throw error.response?.data?.error || error.message || 'Failed to fetch available awards';
  }
};

export const redeemXP = async (points) => {
  try {
    const response = await api.post('/user/redeem-xp', { points });
    return response.data;
  } catch (error) {
    console.error('Redeem XP error:', error);
    throw error.response?.data?.error || error.message || 'Redemption failed';
  }
};

export const awardInspirationPoints = async () => {
  try {
    const response = await api.post('/user/award-inspiration-points');
    return response.data;
  } catch (error) {
    console.error('Award inscription points error:', error);
    throw error.response?.data?.error || error.message || 'Failed to award points';
  }
};

export const awardSpaceJoinPoints = async () => {
  try {
    const response = await api.post('/user/award-space-join-points');
    return response.data;
  } catch (error) {
    console.error('Award space join points error:', error);
    throw error.response?.data?.error || error.message || 'Failed to award points';
  }
};

export const awardLmsCoursePoints = async (courseId) => {
  try {
    const response = await api.post('/user/award-lms-course-points', { courseId });
    return response.data;
  } catch (error) {
    console.error('Award LMS course points error:', error);
    throw error.response?.data?.error || error.message || 'Failed to award LMS points';
  }
};

export const syncLmsProgress = async (progress) => {
  try {
    const response = await api.post('/user/lms-sync', { progress });
    return response.data;
  } catch (error) {
    console.error('Sync LMS progress error:', error);
    throw error.response?.data?.error || error.message || 'Failed to sync LMS progress';
  }
};
